<template>
    <div>
        <div v-click-away="closeDropdown" class="search-autocomplete">
            <div :class="searchAutocompleteClass">
                <div class="search-autocomplete__search--input">
                    <div class="search-autocomplete__input">
                        <input
                            :placeholder="placeholder"
                            :value="value"
                            @input="inputChange($event)"
                            @keyup.enter="enterClicked()"
                            @focusin="focusIn()"
                            @focusout="focusOut()"
                        />
                    </div>
                    <div class="search-autocomplete__actions">
                        <button
                            class="search-autocomplete__search--input-button"
                            :isSubmittable="false"
                            @click="togleSearchDropdown()"
                        >
                            {{ detailSearchTitle }}
                            <common-icon :name="isAddDropdownOpen ? 'arrow-down' : 'arrow-forward'" size="tiny"></common-icon>
                        </button>
                        <button
                            type="quaternary"
                            icon="plus-circle"
                            size="tiny"
                            class="search-autocomplete__button"
                            :isSubmittable="false"
                            @click="enterClicked()"
                        >
                            <common-icon name="search" size="extra-huge"></common-icon>
                        </button>
                    </div>
                </div>
            </div>
            <div v-show="shouldShowResults" v-click-away="hideResults" class="search-autocomplete__results">
                <slot></slot>
            </div>
            <div class="search-autocomplete__body">
                <common-dropdown
                    :isOpen="isAddDropdownOpen"
                    :recalculatePosition="false"
                    arrowPositionX="0"
                    arrowPositionY="0"
                    :hasArrow="false"
                    class="search-autocomplete__dropdown"
                >
                    <div>
                        <common-content-wrapper class="search-autocomplete__result-dropdown" width="large">
                            <offer-filter-form
                                ref="offerFilterForm"
                                class="search-autocomplete__form"
                                :countries="countries"
                                :countryOptions="countryOptions"
                                :allCategories="allCategories"
                                :prefill="filterApplied"
                                @filter-change="enterClicked($event)"
                                @reset-filter="resetFilter()"
                                @close-dropdown-form="closeDropdown"
                            ></offer-filter-form>
                            <common-icon
                                name="arrow-up"
                                size="huge"
                                class="search-autocomplete__dropdown-icon"
                                @click="togleSearchDropdown()"
                            ></common-icon>
                        </common-content-wrapper>
                    </div>
                </common-dropdown>
            </div>
        </div>
        <div v-if="isAddDropdownOpen" class="blur-dropdown"></div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex'
    import debounce from 'lodash.debounce'
    import OfferFilterForm from 'components/forms/offer/OfferFilterForm'
    import ServiceCategoryAPI from 'api/ServiceCategoryAPI'
    import RoutesEnum from 'enums/routes.enum'
    import CountryEnum from 'enums/country.enum'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'

    export default {
        mixins: [WindowResizeMixin],

        components: {
            OfferFilterForm,
        },

        props: {
            placeholder: { type: String, default: '' },
            searchApi: { type: Function, default: () => ({}) },
        },

        data: () => ({
            shouldShowResults: false,
            value: '',
            MIN_LENGTH: 2,
            isEnterClicked: false,
            allCategories: [],
            isAddDropdownOpen: false,
            filterApplied: {},
            isInputInFocus: false,
            allowedKeys: ['countryId', 'cityId', 'fromPrice', 'serviceGroupId', 'serviceId', 'toPrice', 'filter'],
            countryOptions: [],
        }),

        computed: {
            ...mapGetters({
                countries: 'data/getCountries',
            }),

            detailSearchTitle() {
                const numberOfActiveFilters = this.numberOfActiveFilters ? ` (${this.numberOfActiveFilters})` : ''
                return this.isSmartphoneLarge
                    ? `${this.$t('forms.offer.offer_filter.detail')}${numberOfActiveFilters}`
                    : `${this.$t('forms.offer.offer_filter.detail_search')}${numberOfActiveFilters}`
            },

            numberOfActiveFilters() {
                if (this.filterApplied) {
                    return Object.keys(this.filterApplied).filter((key) => this.allowedKeys.includes(key)).length
                } else {
                    return 0
                }
            },

            searchAutocompleteClass() {
                return {
                    'search-autocomplete__search': true,
                    'search-autocomplete__search--focus': this.isInputInFocus,
                }
            },
        },

        methods: {
            fetchCountry() {
                this.countryOptions = Object.entries(CountryEnum).map(([key, value]) => ({
                    value: value,
                    label: this.$t(`forms.company.create_company.fields.country.${key}`),
                }))
            },

            inputChange(event) {
                const value = event.target.value

                this.emitEvent({ name: 'search-change', payload: value })
                this.value = value
                if (value?.length < this.MIN_LENGTH) {
                    this.hideResults()
                    return
                }
                this.serchResultChange(value)
            },

            focusIn() {
                this.isInputInFocus = true
            },

            focusOut() {
                this.isInputInFocus = false
            },

            serchResultChange: debounce(function (value) {
                if (this.isEnterClicked || this.isAddDropdownOpen) {
                    this.isEnterClicked = false
                    return
                }
                this.showResults()
                this.searchApi(value).then((res) => this.emitEvent({ name: 'search-results-change', payload: res }))
            }, 500),

            enterClicked(payload) {
                this.isEnterClicked = true
                this.hideResults()

                if (this.$refs.offerFilterForm && !payload) {
                    this.$refs.offerFilterForm.submit()
                } else {
                    this.emitEvent({ name: 'enter-click', payload })
                    this.closeDropdown()
                }
                this.isAddDropdownOpen = false
            },

            hideResults() {
                this.shouldShowResults = false
            },

            showResults() {
                if (this.value.length >= this.MIN_LENGTH) {
                    setTimeout(() => (this.shouldShowResults = true), 200)
                }
            },

            togleSearchDropdown() {
                this.isAddDropdownOpen = !this.isAddDropdownOpen
                if (this.isAddDropdownOpen) {
                    this.hideResults()
                    document.body.classList.add('search-autocomplete-open')
                } else {
                    document.body.classList.remove('search-autocomplete-open')
                }
            },

            fetchServiceCategories() {
                return ServiceCategoryAPI.getAll().then((categories) => {
                    this.allCategories = categories
                })
            },

            setFilterApplied() {
                this.filterApplied = this.$route.query
                if (this.filterApplied.countryId) {
                    this.filterApplied.countryId = Number(this.filterApplied.countryId)
                }
                if (this.filterApplied.serviceGroupId) {
                    this.filterApplied.serviceGroupId = Number(this.filterApplied.serviceGroupId)
                }
                if (!this.filterApplied.filter) {
                    this.value = undefined
                }
            },

            resetFilter() {
                this.$router.push({ name: RoutesEnum.OFFER.FIND })
                this.togleSearchDropdown()
            },

            closeDropdown() {
                this.isAddDropdownOpen = false
                document.body.classList.remove('search-autocomplete-open')
            },

            prefillSearchValue() {
                if (this.filterApplied.filter) {
                    this.value = this.filterApplied.filter
                }
            },
        },

        watch: {
            '$route.query'() {
                this.setFilterApplied()
            },
        },

        mounted() {
            this.fetchServiceCategories()
            this.setFilterApplied()
            this.prefillSearchValue()
            this.fetchCountry()
        },
    }
</script>
